import { useCallback, useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'GDS_NEO_LAST_ACTIVITY';

export function clearAutoLogoutStorage() {
  localStorage.removeItem(STORAGE_KEY);
}

const SEC = 1000;
const MIN = 60 * SEC;

// 从最后活动到登出的总时间（9小时）
const TIMEOUT_MS = 9 * 60 * MIN;
// 剩余多少秒时弹出警告
const WARN_DURATION_MS = 3 * MIN;
//控制 setInterval 多久轮询一次剩余时间，决定超时检测的精度
const CHECK_INTERVAL_MS = MIN;
//控制用户触发 mousemove/click 等事件时，多久才允许写一次 localStorage，避免高频事件刷爆存储
const THROTTLE_MS = MIN;

export interface UseAutoLogoutReturn {
  warningVisible: boolean;
  remainingSeconds: number;
  onContinue: () => void;
}

export function useAutoLogout(onTimeout: () => void): UseAutoLogoutReturn {
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  const [warningVisible, setWarningVisible] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const warningVisibleRef = useRef(false);

  const getRemaining = useCallback(() => {
    const last = parseInt(localStorage.getItem(STORAGE_KEY) ?? '0', 10);
    return last + TIMEOUT_MS - Date.now();
  }, []);

  const showWarning = useCallback(() => {
    // 对齐时间戳，确保倒计时从整 WARN_DURATION_MS 开始
    localStorage.setItem(
      STORAGE_KEY,
      String(Date.now() - (TIMEOUT_MS - WARN_DURATION_MS))
    );
    warningVisibleRef.current = true;
    setWarningVisible(true);
    setRemainingSeconds(WARN_DURATION_MS / 1000);
  }, []);

  const updateActivity = useCallback(() => {
    if (warningVisibleRef.current) return;
    const now = Date.now();
    const last = parseInt(localStorage.getItem(STORAGE_KEY) ?? '0', 10);
    if (now - last > THROTTLE_MS) {
      localStorage.setItem(STORAGE_KEY, String(now));
    }
  }, []);

  const onContinue = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    warningVisibleRef.current = false;
    setWarningVisible(false);
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);

    if (!stored) {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } else {
      const remaining = getRemaining();
      if (remaining <= 0) {
        localStorage.removeItem(STORAGE_KEY);
        onTimeoutRef.current();
        return;
      }
    }

    const events = [
      'mousemove',
      'keydown',
      'click',
      'scroll',
      'touchstart'
    ] as const;
    events.forEach(ev =>
      window.addEventListener(ev, updateActivity, { passive: true })
    );

    // 其他标签页点「続ける」更新了 key → 关闭本标签页弹窗
    const handleStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      if (e.newValue && warningVisibleRef.current) {
        warningVisibleRef.current = false;
        setWarningVisible(false);
      }
    };
    window.addEventListener('storage', handleStorage);

    const interval = setInterval(() => {
      // 其他标签页已触发登出（key 被移除）→ 不重复调用
      if (!localStorage.getItem(STORAGE_KEY)) return;
      const remaining = getRemaining();
      if (remaining <= 0) {
        localStorage.removeItem(STORAGE_KEY);
        onTimeoutRef.current();
      } else if (remaining <= WARN_DURATION_MS && !warningVisibleRef.current) {
        showWarning();
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      events.forEach(ev => window.removeEventListener(ev, updateActivity));
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, [getRemaining, showWarning, updateActivity]);

  useEffect(() => {
    if (!warningVisible) return;
    const tick = setInterval(() => {
      const remaining = getRemaining();
      if (remaining <= 0) {
        clearInterval(tick);
        localStorage.removeItem(STORAGE_KEY);
        warningVisibleRef.current = false;
        setWarningVisible(false);
        onTimeoutRef.current();
      } else {
        setRemainingSeconds(Math.ceil(remaining / SEC));
      }
    }, 1000);
    return () => clearInterval(tick);
  }, [warningVisible, getRemaining]);

  return { warningVisible, remainingSeconds, onContinue };
}
